<template>
  <div class="card has-padding-20 has-padding-left-0">
    <validation-observer
      ref="shipperConsigneeDetailsObserver"
      tag="div"
      v-slot="{invalid, valid, untouched, errors}"
    >
      <div class="card-content">
        <div class="has-margin-bottom-40 has-margin-top-10 has-border-bottom">
          <h2
            class="is-size-4 has-text-weight-bold is-uppercase has-letter-spacing-3 is-black"
          >Shipper and Consignee Details</h2>
        </div>
        <!-- 1st Form Inputs -->
        <div class="columns is-multiline">
          <!-- Shipper Name -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required'"
              :model="shipperConsigneeDetails.shipper_name"
              :vuexKey="'shipper_name'"
              :label="'Shipper/Consignor/Sender Name'"
              :errorLabel="`Shipper's Name`"
              :name="'shipperName'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Shippers Address -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required'"
              :model="shipperConsigneeDetails.shipper_address"
              :vuexKey="'shipper_address'"
              :label="'Shipper/Consignor/Sender Address'"
              :errorLabel="`Shipper's Address`"
              :name="'shipperAddress'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Phone Number -->
          <div class="column is-6">
            <phone-number-input
              :rules="'required'"
              :model="shipperConsigneeDetails.shipper_phone_number"
              :vuexKey="'shipper_phone_number'"
              :label="'Shipper/Consignor/Sender Phone Number'"
              :errorLabel="`Phone Number`"
              :name="'shipperPhoneNumber'"
              @input="updateVuex"
            ></phone-number-input>
          </div>

          <!-- Email Address -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required|email'"
              :model="shipperConsigneeDetails.shipper_email"
              :vuexKey="'shipper_email'"
              :label="'Shipper/Consignor/Sender Email Address'"
              :errorLabel="`Email Address`"
              :name="'shipperEmail'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Consignee Name -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required'"
              :model="shipperConsigneeDetails.consignee_name"
              :vuexKey="'consignee_name'"
              :label="'Consignee Name'"
              :errorLabel="`Consignee Name`"
              :name="'consigneeName'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Consignee Address -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required'"
              :model="shipperConsigneeDetails.consignee_address"
              :vuexKey="'consignee_address'"
              :label="'Consignee Address'"
              :errorLabel="`Consignee Address`"
              :name="'consigneeAddress'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Consignee Phone Number -->
          <div class="column is-6">
            <phone-number-input
              :rules="'required'"
              :model="shipperConsigneeDetails.consignee_phone_number"
              :vuexKey="'consignee_phone_number'"
              :label="'Consignee Phone Number'"
              :errorLabel="`Phone Number`"
              :name="'consigneePhoneNumber'"
              @input="updateVuex"
            ></phone-number-input>
          </div>

          <!-- Consignee Email Address -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required|email'"
              :model="shipperConsigneeDetails.consignee_email"
              :vuexKey="'consignee_email'"
              :label="'Consignee Email Address'"
              :errorLabel="`Email Address`"
              :name="'consigneeEmail'"
              @input="updateVuex"
            ></input-with-validation>
          </div>
        </div>

        <!-- EOF 1st Form Inputs -->
        <div class="has-margin-bottom-40 has-margin-top-40 has-border-bottom">
          <h2
            class="is-size-4 has-text-weight-bold is-uppercase has-letter-spacing-3 is-black"
          >Ship and Vessel Details</h2>
        </div>
        <!-- 2nd Form Inputs -->
        <div class="columns is-multiline">
          <!-- Ship Name -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required'"
              :model="shipperConsigneeDetails.ship_name"
              :vuexKey="'ship_name'"
              :label="`Ship's Name`"
              :errorLabel="`Ship Name`"
              :name="'shipName'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Voyage Number -->
          <div class="column is-6">
            <input-with-validation
              :rules="'required'"
              :model="shipperConsigneeDetails.voyage_number"
              :vuexKey="'voyage_number'"
              :label="`Voyage Number`"
              :errorLabel="`Voyage Number`"
              :name="'voyageNumber'"
              @input="updateVuex"
            ></input-with-validation>
          </div>

          <!-- Port of Loading Origin -->
          <div class="column is-6">
            <ValidationProvider
              :rules="'required'"
              v-slot="{ errors, valid }"
              :name="'port of loading origin'"
            >
              <b-field
                :label="'PORT OF LOADING ORIGIN'"
                :type="{'is-danger': errors.length }"
                :message="errors[0]"
              >
                <multiselect
                  v-model="portOfLoadingOrigin"
                  deselect-label="Selected"
                  track-by="id"
                  label="label"
                  placeholder="Select a Value"
                  :name="'port of loading origin'"
                  :options="portOfLoadingOriginOptions"
                  :searchable="true"
                  :allow-empty="true"
                  @select="(option,id) => updateVuex({key: 'port_of_loading_origin', value: option.value})"
                >
                  <template slot="singleLabel" slot-scope="{ option }">{{ option.label }}</template>
                </multiselect>
              </b-field>
            </ValidationProvider>
          </div>

          <!-- Port of loading -->
          <template v-if="portOfLoadingOrigin">
            <!-- Nigerian Port of Loading -->
            <div class="column is-6" v-if="portOfLoadingOrigin.value === 'NIGERIA' ">
              <ValidationProvider
                :rules="'required'"
                v-slot="{ errors, valid }"
                :name="'port of loading'"
              >
                <b-field
                  :label="'Port of Loading'"
                  :type="{'is-danger': errors.length }"
                  :message="errors[0]"
                >
                  <multiselect
                    v-model="selectedPortOfLoading"
                    deselect-label="Port must be selected"
                    track-by="id"
                    label="name"
                    placeholder="Select a Port"
                    :name="'port of loading'"
                    :options="ports"
                    :searchable="true"
                    :allow-empty="false"
                    @select="(option,id) => updatePortOfLoadingID(option)"
                  >
                    <template slot="singleLabel" slot-scope="{ option }">{{ option.name }}</template>
                  </multiselect>
                </b-field>
              </ValidationProvider>
            </div>

            <!-- International Port of Loading -->
            <div class="column is-6" v-if="portOfLoadingOrigin.value === 'INTERNATIONAL' ">
              <input-with-validation
                :rules="'required'"
                :model="shipperConsigneeDetails.port_of_loading"
                :vuexKey="'port_of_loading'"
                :label="`Port of Loading`"
                :errorLabel="`Port of Loading`"
                :name="'portOfLoading'"
                @input="updateVuex"
              ></input-with-validation>
            </div>
          </template>

          <!-- Port of Discharge -->
          <div class="column is-6">
            <ValidationProvider
              :rules="'required'"
              v-slot="{ errors, valid }"
              :name="'port of discharge'"
            >
              <b-field
                :label="'Port of Discharge'"
                :type="{'is-danger': errors.length }"
                :message="errors[0]"
              >
                <multiselect
                  v-model="selectedPortOfDischarge"
                  deselect-label="Port must be selected"
                  track-by="id"
                  label="name"
                  placeholder="Select a Port"
                  :name="'port of discharge'"
                  :options="ports"
                  :searchable="true"
                  :allow-empty="false"
                  @select="(option,id) => updatePortOfDischargeID(option)"
                >
                  <template slot="singleLabel" slot-scope="{ option }">{{ option.name }}</template>
                </multiselect>
              </b-field>
            </ValidationProvider>
          </div>

          <!-- Port of Call -->
          <div class="column is-6">
            <input-with-validation
              :rules="''"
              :model="shipperConsigneeDetails.port_of_call"
              :vuexKey="'port_of_call'"
              :label="'Final Destination - Offshore(Optional)'"
              :errorLabel="`Port of Call`"
              :name="'portOfCall'"
              :placeholder="'Port of Call'"
              @input="updateVuex"
            ></input-with-validation>
          </div>
        </div>

        <div class="has-margin-bottom-40 has-margin-top-40 has-border-bottom">
          <h2
            class="is-size-4 has-text-weight-bold is-uppercase has-letter-spacing-3 is-black"
          >Shipping Dates</h2>
        </div>

        <div class="columns is-multiline">
          <!-- Date of Departure -->
          <div class="column is-6">
            <date-input-with-validation
              :rules="'required|date_format:dd/MM/yyyy'"
              :model="dateOfDeparture"
              :vuexKey="'date_of_departure'"
              :label="'Expected Date of Departure'"
              :errorLabel="`Date of Departure`"
              :name="'dateOfDeparture'"
              :placeholder="'Date of Departure'"
              @input="updateVuex"
            ></date-input-with-validation>
          </div>

          <!-- Date of Arrival -->
          <div class="column is-6">
            <date-input-with-validation
              :rules="'required|date_format:dd/MM/yyyy'"
              :model="dateOfArrival"
              :vuexKey="'date_of_arrival'"
              :label="'Expected Date of Arrival'"
              :errorLabel="`Date of Arrival`"
              :name="'dateOfArrival'"
              :placeholder="'Date of Arrival'"
              :disabledDates="disableDate"
              @input="updateVuex"
            ></date-input-with-validation>
          </div>
        </div>
      </div>

      <!-- Form Buttons -->
      <div class="steps-actions has-margin-top-30">
        <div class="steps-action is-invisible">
          <a href="#" class="button is-danger" @click="previous">Previous</a>
        </div>
        <div class="steps-action">
          <!-- <a href="#" class="button is-primary" @click="next">Next</a> -->
          <button class="button is-primary" @click="next" :disabled="invalid">Next</button>
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Multiselect from "vue-multiselect";
import InputWithValidation from "./form-inputs/InputWithValidation";
import PhoneNumberInput from "./form-inputs/PhoneNumberInput";
import DateInputWithValidation from "./form-inputs/DateInputWithValidation";
import * as moment from "moment";
import isValidDate from "@/modules/commonModule/helpers/isValidDate";
import { SHIPPER_MUTATION_TYPES } from "@/store/mutation-types";
import { createNamespacedHelpers } from "vuex";
const { mapMutations } = createNamespacedHelpers("shippers");

export default {
  name: "ShipperAndConsigneeDetails",

  components: {
    ValidationObserver,
    InputWithValidation,
    Multiselect,
    PhoneNumberInput,
    DateInputWithValidation,
    ValidationProvider
  },

  props: {
    shipperConsigneeDetails: { type: Object, required: true }, // Object containing shipper consignee details
    ports: { type: Array, required: true },
    moveToNext: { type: Function },
    moveToPrevious: { type: Function }
  },
  data() {
    return {
      selectedPortOfDischarge: null,
      departureDate: null,
      selectedPortOfLoading: null,
      portOfLoadingOrigin: null,
      portOfLoadingOriginOptions: [
        { id: 1, value: "NIGERIA", label: "NIGERIA" },
        { id: 2, value: "INTERNATIONAL", label: "INTERNATIONAL" }
      ]
    };
  },

  mounted() {
    this.selectedPortOfDischarge = this.ports.find(port => {
      return port.id === this.shipperConsigneeDetails.port_of_discharge_id;
    });

    this.portOfLoadingOrigin = this.portOfLoadingOriginOptions.find(option => {
      return (
        option.value === this.shipperConsigneeDetails.port_of_loading_origin
      );
    });

    if (this.portOfLoadingOrigin === "NIGERIA") {
      this.selectedPortOfLoading = this.ports.find(port => {
        return port.id === this.shipperConsigneeDetails.port_of_loading_id;
      });
    }
  },

  computed: {
    disableDate() {
      const today = new Date();
      const disabledDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      if (this.departureDate && this.departureDate > disabledDate) {
        return { to: this.departureDate };
      } else {
        return { to: disabledDate };
      }
    },

    /**
     * Format the date of Arrival to a format that can be used to "prefill" the date picker
     */
    dateOfArrival() {
      let date = moment(
        this.shipperConsigneeDetails.date_of_arrival,
        "YYYY-MM-DD"
      );
      const date_of_arrival = new Date(date.year(), date.month(), date.date());
      if (isValidDate(date_of_arrival)) {
        return date_of_arrival;
      } else {
        return null;
      }
    },

    /**
     * Format the date of Arrival to a format that can be used to "prefill" the date picker
     */
    dateOfDeparture() {
      let date = moment(
        this.shipperConsigneeDetails.date_of_departure,
        "YYYY-MM-DD"
      );
      const date_of_departure = new Date(
        date.year(),
        date.month(),
        date.date()
      );
      if (isValidDate(date_of_departure)) {
        this.departureDate = date_of_departure;
        return date_of_departure;
      } else {
        return null;
      }
    }
  },

  methods: {
    ...mapMutations({
      commitShipperConsigneeState:
        SHIPPER_MUTATION_TYPES.UPDATE_SHIPPER_AND_CONSIGNEE_DETAILS
    }),
    /**
     * Move to the previous step in the ShippingRequest form.
     */
    previous() {
      this.moveToPrevious();
    },

    /**
     * Validate the ShipperConsigneeDetails form before moving to the next step in the ShippingRequest process.
     */
    async next() {
      const isValid = await this.$refs.shipperConsigneeDetailsObserver.validate();
      if (!isValid) {
        this.$toast.open({
          duration: 4000,
          position: "is-top-right",
          message:
            "Shipper and consignee details form is incomplete. Please review.",
          type: "is-danger"
        });

        return;
      }
      this.moveToNext();
    },

    /**
     * Update the shipper and consignee details in the vuex store
     */
    updateVuex({ key, value }) {
      this.commitShipperConsigneeState({ key, value });
    },

    updatePortOfDischargeID(option) {
      this.updateVuex({ key: "port_of_discharge_id", value: option.id });
    },

    updatePortOfLoadingID(option) {
      this.updateVuex({ key: "port_of_loading_id", value: option.id });
    }
  }
};
</script>


<style lang="scss" scoped>
</style>
